import { authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import { useQuery } from '@tanstack/react-query'

export type PlatformPulsePeriod = '7d' | '30d'

export type SkillPoolHighlight = {
  coverage_ratio: number
  catalog_use_ratio: number
  concentration_ratio: number
  depth_ratio: number
  uncovered_profile_count: number
  profile_count: number
  covered_profile_count: number
  published_skills: number
  possessed_published_skills: number
  empty_catalog: boolean
  empty_pool: boolean
  message: string
  top_domains: Array<{
    id: string
    key: string
    label: string
    candidate_count: number
    skill_count: number
  }>
  report_path: string
  lexicon_path: string
}

export type PlatformPulseReport = {
  period: { label: string; start: string; end: string }
  skill_pool: SkillPoolHighlight
  jobs_in_play: {
    managed: { draft: number; published: number; closed: number; archived: number }
    marketplace: { active: number; expired: number }
    published_with_applications: number
    stale_published_no_application: number
    stale_days: number
  }
  fulfillment: {
    hires_in_period: number
    median_days_publish_to_hire: number | null
    hire_samples_for_median: number
    pipeline: {
      applied: number
      in_review: number
      interview: number
      offer: number
    }
  }
  ingest: {
    enabled_terms: number
    total_terms: number
    failing_terms: number
    last_sync_at: string | null
    last_sync_status: string
    last_error?: string
    failing_provider_errors?: Array<{ term: string; provider: string; error: string }>
    marketplace_active: number
    marketplace_expired: number
    admin_path: string
  }
  marketplace_apply_clicks?: {
    total: number
    by_provider: Record<string, number>
  }
  marketplace_interests: {
    total: number
    with_note: number
    blank_note: number
    by_provider: Record<string, number>
    recent: Array<{
      id: string
      candidate_name: string
      candidate_email: string
      opportunity_id: string
      job_title: string
      provider: string
      note: string
      has_note: boolean
      created_at: string
      job_path: string
    }>
    recent_limit: number
    list_path: string
  }
  activity: {
    signups: number
    recent_logins: number
    applications: number
    managed_published: number
    engagement_path: string
  }
  rubrics: {
    corpora: Array<{
      id: string
      slug: string
      name: string
      status: string
      methodology_id: string
      current_version: number | null
      is_tenant_default: boolean
    }>
    admin_path: string
  }
  golden_set?: GoldenSetPulse
}

export type GoldenSetPulse = {
  has_run: boolean
  run_id?: string | null
  mode?: string
  model?: string
  golden_set_version?: string
  run_at?: string | null
  stale?: boolean
  pair_count?: number
  labelled_count?: number
  agreed_count?: number
  agreement_ratio?: number | null
  mean_abs_drift?: number | null
  band_changes?: number
  distribution_shift?: number | null
  max_stdev?: number | null
  flagged?: string[]
  command?: string
}

export function usePlatformPulse(period: PlatformPulsePeriod) {
  return useQuery({
    queryKey: ['talentco', 'platform-pulse', period],
    queryFn: async (): Promise<PlatformPulseReport> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/talentco/platform-pulse?period=${period}`,
      )
      const body = await response.json()
      if (!response.ok) {
        const error = new Error(
          (body as { error?: string; detail?: string }).error
            || (body as { detail?: string }).detail
            || `Failed to load platform pulse (${response.status})`,
        )
        ;(error as Error & { status?: number }).status = response.status
        throw error
      }
      return body as PlatformPulseReport
    },
  })
}
